/**
 * Wrapper IndexedDB pour l'historique des conversations AI.
 *
 * Base : orbit_ai  (version 2)
 * Stores :
 *   - conversations         : chat RAG (Ragflow / cdg31)
 *   - builder_conversations : chat agentique du Page Builder (Lot 6.4)
 *
 * Les deux stores partagent la même forme : { id, updatedAt, ... } —
 * mais sont séparés pour isoler leurs domaines (un purge / clear sur l'un
 * ne touche pas l'autre, et un mauvais format dans l'un ne contamine pas
 * l'historique de l'autre).
 */

const DB_NAME = 'orbit_ai'
const DB_VERSION = 2
const STORE = 'conversations'
const BUILDER_STORE = 'builder_conversations'
const MAX_HISTORY = 15

let dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)

    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('updatedAt', 'updatedAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(BUILDER_STORE)) {
        const store = db.createObjectStore(BUILDER_STORE, { keyPath: 'id' })
        store.createIndex('updatedAt', 'updatedAt', { unique: false })
      }
    }

    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error)
    }
  })
  return dbPromise
}

/**
 * Generic loader used by both stores.
 */
async function loadFromStore(storeName) {
  try {
    const db = await openDB()
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly')
      const index = tx.objectStore(storeName).index('updatedAt')
      const req = index.openCursor(null, 'prev')
      const results = []

      req.onsuccess = (e) => {
        const cursor = e.target.result
        if (cursor && results.length < MAX_HISTORY) {
          results.push(cursor.value)
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      req.onerror = () => reject(req.error)
    })
  } catch {
    return []
  }
}

/**
 * Generic upserter used by both stores. Purges oldest entries past MAX_HISTORY.
 */
async function saveToStore(storeName, entry) {
  try {
    const db = await openDB()
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)

    store.put(entry)

    const countReq = store.count()
    countReq.onsuccess = () => {
      if (countReq.result <= MAX_HISTORY) return
      const index = store.index('updatedAt')
      const cursorReq = index.openCursor(null, 'next')
      let toDelete = countReq.result - MAX_HISTORY

      cursorReq.onsuccess = (e) => {
        const cursor = e.target.result
        if (!cursor || toDelete <= 0) return
        cursor.delete()
        toDelete--
        cursor.continue()
      }
    }
  } catch {
    // Ignore — IndexedDB non disponible (private browsing, etc.)
  }
}

async function deleteFromStore(storeName, id) {
  try {
    const db = await openDB()
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite')
      const req = tx.objectStore(storeName).delete(id)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  } catch {
    // Ignore
  }
}

// RAG chat (Ragflow / cdg31) — backward-compatible APIs.

export const loadHistory = () => loadFromStore(STORE)
export const saveConversation = (entry) => saveToStore(STORE, entry)
export const deleteConversation = (id) => deleteFromStore(STORE, id)

// Builder chat (Lot 6.4) — separate store so the two histories don't mix.

export const loadBuilderHistory = () => loadFromStore(BUILDER_STORE)
export const saveBuilderConversation = (entry) => saveToStore(BUILDER_STORE, entry)
export const deleteBuilderConversation = (id) => deleteFromStore(BUILDER_STORE, id)
